import React, { useContext, useEffect, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useMutation } from '@apollo/client';

import DocsCard from 'app/src/components/Docs/Card';
import PrintOverlayDoc from 'app/src/components/Docs/Tags/PrintOverlay';
import { Form, Field, Label } from 'app/src/components/Orders/PrintSetup';
import ClientContext from 'app/src/contexts/ClientContext';
import { Button } from 'app/src/elements/buttons';
import { StyledTextInput } from 'app/src/elements/inputs';
import { Title, Text } from 'app/src/styles';
import { rest } from 'app/src/utils/rest';

import PrintTemplate from './PrintTemplate';
import { UPDATE_PRINT_OFFSETS } from './queries';

const Alignment = () => {
  const [printOffsetX, setPrintOffsetX] = useState(0);
  const [printOffsetY, setPrintOffsetY] = useState(0);
  const [previewType, setPreviewType] = useState();

  const {
    credentials, currentUser, hasPolicy, refetchCurrentUser, isMobile,
  } = useContext(ClientContext);
  const iframeRef = useRef();

  const [updatePrintOffsets] = useMutation(UPDATE_PRINT_OFFSETS, {
    onCompleted: () => {
      refetchCurrentUser();
      previewOverlay({ type: previewType });
    }
  });

  const setupIframe = blob => {
    const dataUrl = window.URL.createObjectURL(blob);
    iframeRef.current.setAttribute('src', dataUrl);
  };

  const previewOverlay = ({ type }) => {
    setPreviewType(type);

    rest({
      path: `pdf/alignment-overlay.pdf?type=${type}`,
      options: { headers: { Accept: 'application/pdf' } },
      credentials,
    }).then(response => {
      response.blob().then(blob => setupIframe(blob));
    });
  };

  useEffect(() => {
    previewOverlay({ type: 'car' });
  }, []);

  useEffect(() => {
    if (currentUser && !printOffsetX && !printOffsetY) {
      setPrintOffsetX(currentUser.printOffsetX);
      setPrintOffsetY(currentUser.printOffsetY);
    }
  }, [currentUser]);

  const submitPrintOffsets = () => {
    updatePrintOffsets({
      variables: {
        printOffsetX: parseFloat(printOffsetX) || 0.0,
        printOffsetY: parseFloat(printOffsetY) || 0.0,
      }
    });
  };

  return (
    <View style={{ height: '80vh' }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Title style={{ flex: 1 }}>Print Alignment</Title>
        <DocsCard title="Documentation" element={PrintOverlayDoc} />
      </View>

      {hasPolicy({ type: 'Order', action: 'print' }) && (
        <PrintTemplate isMobile={isMobile} />
      )}

      <Text>
        Depending on your printer setup, the purchase information may not
        exactly align. You can adjust the offsets below to improve alignment.
        You can print the PDF below to assist in determining adjustments and
        printer setup. You may need to configure your printer to print on 6" x
        12" paper (the size of a car tag--4" x 7" for Motorcycle tags). The
        grid areas represent places where purchase information will print. The
        grid lines are 1/8" apart. You can also pick up alignment template
        sheets at the OIADA/ADR office.
      </Text>

      <Form isMobile={isMobile} style={{ marginTop: 6, marginBottom: 6 }}>
        <Field>
          <Label>Horizontal Offset (inches):</Label>
          <StyledTextInput
            value={printOffsetX}
            onChangeText={setPrintOffsetX}
            style={{ width: 60 }}
          />
        </Field>

        <Field>
          <Label>Vertical Offset (inches):</Label>
          <StyledTextInput
            value={printOffsetY}
            onChangeText={setPrintOffsetY}
            style={{ width: 60 }}
          />
        </Field>

        <Button
          label="Save Offsets"
          onPress={submitPrintOffsets}
        />
      </Form>

      <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 20 }}>
        <Pressable onPress={() => previewOverlay({ type: 'car' })}>
          <Text>Show Car Tag Alignment PDF</Text>
        </Pressable>

        <Pressable onPress={() => previewOverlay({ type: 'motorcycle' })}>
          <Text>Show Motorcycle Tag Alignment PDF</Text>
        </Pressable>
      </View>

      <iframe ref={iframeRef} width="100%" height="100%"></iframe>

      <Button
        label="Print Alignment PDF"
        onPress={() => iframeRef.current.contentWindow.print()}
        style={{ alignSelf: 'flex-end' }}
      />
    </View>
  );
};

export default Alignment;
